/**
 * Validation schemas for signal feedback mutations.
 *
 * Used both by the frontend (before calling Edge Functions) and by the
 * Edge Functions (defensive re-validation). Mirrors the Zod-based pattern
 * established in Stream 0 (shared/validation/*).
 *
 * The Edge Function in supabase/functions/archive-signal/index.ts performs
 * its own validation using plain TS guards rather than importing Zod into
 * Deno; this module is the canonical frontend-side validator and the
 * source of truth for the schema shape.
 */

import { z } from 'zod';

import { ARCHIVE_REASONS } from '../types/signal-durability';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const archiveSignalSchema = z.object({
  signal_id: z.string().regex(UUID_RE, 'signal_id must be a UUID'),
  reason: z.enum(ARCHIVE_REASONS),
  notes: z.string().max(2000, 'notes must be 2000 characters or fewer').optional(),
});

export type ArchiveSignalInput = z.infer<typeof archiveSignalSchema>;

export const restoreSignalSchema = z.object({
  signal_id: z.string().regex(UUID_RE, 'signal_id must be a UUID'),
});

export type RestoreSignalInput = z.infer<typeof restoreSignalSchema>;

/**
 * Validate an archive payload. Throws ZodError on failure.
 */
export function parseArchiveSignal(input: unknown): ArchiveSignalInput {
  return archiveSignalSchema.parse(input);
}

/**
 * Validate a restore payload. Throws ZodError on failure.
 */
export function parseRestoreSignal(input: unknown): RestoreSignalInput {
  return restoreSignalSchema.parse(input);
}

/**
 * Safe-parse variant for use in forms / handlers where you want to
 * convert validation errors into UI messages without exceptions.
 */
export function safeParseArchiveSignal(input: unknown):
  | { ok: true; data: ArchiveSignalInput }
  | { ok: false; error: string } {
  const r = archiveSignalSchema.safeParse(input);
  if (r.success) return { ok: true, data: r.data };
  return { ok: false, error: r.error.issues[0]?.message ?? 'Invalid input' };
}

export function safeParseRestoreSignal(input: unknown):
  | { ok: true; data: RestoreSignalInput }
  | { ok: false; error: string } {
  const r = restoreSignalSchema.safeParse(input);
  if (r.success) return { ok: true, data: r.data };
  return { ok: false, error: r.error.issues[0]?.message ?? 'Invalid input' };
}
